import { Component, OnDestroy, OnInit } from '@angular/core';
import Highcharts from 'highcharts';
import { CacheService, ParameterHandlerService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { groupBy, hexToRGB } from 'projects/common/src/app/services/utils/utilities';
import { IShift } from 'src/app/modules/core/interfaces';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { IProductionFront } from 'src/app/modules/widget/interfaces/production-front';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { CommunicationService } from 'src/app/services/communication';
import { DashboardWidgetChartComponent } from '../../dashboard-widget-chart/dashboard-widget-chart.component';

@Component({
  selector: 'app-production-by-front',
  templateUrl: './production-by-front.component.html',
  styleUrls: ['./production-by-front.component.scss'],
})
export class ProductionByFrontComponent extends DashboardWidgetChartComponent implements OnInit, OnDestroy {
  public shifts: IProductionFront[][];
  public displayMessage = false;

  constructor(
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    private datePipe: DatePresentationPipe,
    protected screenViewService: ScreenViewService,
    private languageService: LanguageService,
  ) {
    super(progressBarService, communication, parameterService, sidebarService, cacheService, machineContext, screenViewService);
    this.shifts = [];
    this.initRequestParams();
  }

  /**
   * Init request params
   */
  protected initRequestParams(): void {
    this.params = {
      hasShift: true,
      hasCompany: true,
      hasDate: true,
      hasZone: true,
    };
  }

  /**
   * Function to draw the chart
   *
   * @param chartOptions ChartOptions
   */
  public drawView(chartOptions: any): void {
    this.displayMessage = !chartOptions.length;
    const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';
    const date = this.parameterService.parameters.item('date').value;
    const shift = this.parameterService.parameters.item('shift').value as IShift;
    const user = this.parameterService.parameters.item('user-data').value;

    this.shifts = Object.values(groupBy(chartOptions, (x) => x.shiftId));

    const categories = [];

    let i = 0;

    const series = [];
    const allZones = Object.values(groupBy(chartOptions, (x) => x.zoneId)) as any[][];

    for (const zone of allZones) {
      categories.push(`${zone[0].zoneName}//${zone[0].parentZoneName}`);
    }

    let shiftCollection = null;
    if (shift.shiftId === -1) {
      shiftCollection = this.parameterService.parameters.item('shift-collection').value as IShift[];
    }

    if (shiftCollection) {
      for (const currentShift of shiftCollection) {
        const newValues = [];
        for (const zone of allZones) {
          const mapped: number[] = zone.map((value) => {
            if (Number(value.shiftId) === 0 || Number(value.shiftId) === Number(currentShift.shiftId)) {
              return value.depth;
            }
            return 0;
          });
          newValues.push(mapped.reduce((a, b) => a + b));
        }

        series.push({
          name: currentShift.name,
          data: newValues.map((value) => (value === 0 ? null : value)),
          stack: 'A',
          color: hexToRGB(currentShift.color.replace('%', '#'), '0.5'),
          borderColor: hexToRGB(currentShift.color.replace('%', '#'), '0.5'),
          borderWidht: 2,
          showInLegend: false,
        });
      }
    } else {
      const newValues = [];

      for (const zone of allZones) {
        const mapped: number[] = zone.map((value) => {
          if (Number(value.shiftId) === 0 || Number(value.shiftId) === Number(shift.shiftId)) {
            return value.depth;
          }
          return 0;
        });

        newValues.push(mapped.reduce((a, b) => a + b));
      }

      series.push({
        name: shift.name,
        data: newValues.map((value) => (value === 0 ? null : value)),
        stack: 'A',
        borderWidht: 2,
        color: hexToRGB(shift.color.replace('%', '#'), '0.5'),
        borderColor: shift.color.replace('%', '#'),
        showInLegend: false,
      });
    }

    for (const category of categories) {
      if (i % 2 === 0) {
        categories[i] = category.split('//')[0];
      }
      i++;
    }

    const data = {
      chart: {
        type: 'column',
        zoomType: 'xy',
        backgroundColor: '#F8F8F8',
      },
      title: {
        text: '',
        margin: 50,
      },
      // subtitle: {
      //   // TODO traducir
      //   text: `Empresa: ${user.company.name}, Equipo: ${machineType.name}, Fecha: ${this.datePipe.transform(date)}, Turno: ${shift.name}`,
      // },
      subtitle: {
        text: `${this.languageService.message('company')}: ${user.company.name},
              ${this.languageService.message('device')}: ${this.languageService.message(machineType.name)},
              ${this.languageService.message('date')}: ${this.datePipe.transform(date)},
              ${this.languageService.message('shift_')}: ${shift.name}`,
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories,
        labels: {
          useHTML: true,
          labelFormatter(): string {
            const values = String(this.value).split('//');
            let divStreet = '';
            if (values[1]) {
              divStreet = `
                <div style="margin-left: -100%; font-weight: 600">
                  ${values[1] ? values[1].replace('CA-HD-', '') : ''}
                </div>
              `;
            }

            return `
                <div style="font-size: 10px;">
                  <span style="font-style: italic">${values[0].replace('CAB-HD-', '')}</span>
                  <br>
                  ${divStreet}
                </div>
              `;
          },
        },
        title: {
          text: this.languageService.message('streets'),
        },
      },
      yAxis: {
        allowDecimals: false,
        min: 0,
        title: {
          text: this.languageService.message('mts-drill').toUpperCase(),
        },
        stackLabels: {
          style: {
            color: '#707070',
          },
          enabled: true,
        },
      },
      tooltip: {
        backgroundColor: '#433e3e',
        borderColor: '#433e3e',
        borderRadius: 2,
        style: {
          color: 'white',
        },
        useHTML: true,
        labelFormatter(): string {
          const divSymbol = `<div style="background-color: ${this.series.userOptions.color};
                                width: 35px;
                                height: 15px;
                                border: 1px solid ${this.series.userOptions.color};
                                float: left;
                                opacity: 0.7;
                                margin-top: 2px">
                            </div>`;

          return `<div style="width: 200px">
                    <strong>TURNO ${this.series.userOptions.name}</strong> <br>
                    <span>${String(this.key).replace('//', ' / ')}</span> <br>
                    ${divSymbol}
                    <span>&nbsp; ${this.y} mts perforados</span>
                  </div>`;
        },
      },
      plotOptions: {
        column: {
          stacking: 'normal',
        },
      },
      series,
    };
    this.chart = Highcharts.chart(this.chartTarget.nativeElement, data as any, (chart) => {
      /* if (!this.hasData) {
        const newX = chart.plotWidth / 3 + chart.plotLeft;
        const newY = chart.plotHeight / 2 + chart.plotTop;
        chart.renderer.text('La solicitud no arrojó resultados', newX, newY)
          .css({
            color: '#4572A7',
            fontSize: '24px'
          })
          .add();
      } */
    });
  }

  /**
   * Function to display the legend box
   *
   * @param shift Current shift
   */
  public styleShiftLegend(shift: IProductionFront): any {
    return {
      'background-color': hexToRGB(shift.shiftColor, '0.5'),
      border: `2px solid ${shift.shiftColor}`,
    };
  }

  /**
   * On destroy component
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
